import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d70id7r2m2156g4s6t1g';

const plans = [
  {
    name: 'Essential Plan',
    price: '$9.99/mo',
    annual: '$119/yr',
    color: 'border-[#E2E8F0]',
    badge: null,
    features: [
      '1 HVAC tune-up per year',
      'Annual safety inspection',
      'Priority scheduling',
      '10% off all repairs',
      'No overtime charges',
      'Transferable to new owner',
    ],
  },
  {
    name: 'Comfort Plan',
    price: '$19.99/mo',
    annual: '$239/yr',
    color: 'border-[#C41E3A]',
    badge: 'Most Popular',
    features: [
      '2 HVAC tune-ups per year',
      'Annual plumbing inspection',
      'Electrical safety check',
      'Priority emergency service',
      '15% off parts & labor',
      'No overtime charges',
      'Free filter replacements',
      'Transferable to new owner',
    ],
  },
  {
    name: 'Total Home Plan',
    price: '$34.99/mo',
    annual: '$419/yr',
    color: 'border-[#0C2340]',
    badge: 'Best Value',
    features: [
      'Full HVAC, plumbing & electrical',
      'Quarterly system inspections',
      'Emergency priority dispatch',
      '20% off all parts & labor',
      'Free water heater check',
      'Annual duct cleaning',
      'No overtime charges',
      'Free filter replacements',
      'Transferable to new owner',
    ],
  },
];

const MaintenancePlansPage = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('Comfort Plan');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
    setStatus('submitting');
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#0C2340] py-20 nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Protection Plans</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5">The Renova Plan</h1>
          <p className="text-xl text-white/75 max-w-2xl mx-auto">Annual protection for every system in your home. Priority service, maintenance, and exclusive savings.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {plans.map((p) => (
              <div key={p.name} className={`relative rounded-2xl border-2 ${p.color} p-8`}>
                {p.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white ${p.badge === 'Most Popular' ? 'bg-[#C41E3A]' : 'bg-[#0C2340]'}`}>{p.badge}</div>
                )}
                <h3 className="text-xl font-bold text-[#111827] mb-2">{p.name}</h3>
                <p className="text-3xl font-bold text-[#0C2340] mb-1">{p.price}</p>
                <p className="text-sm text-[#6B7280] mb-6">Billed {p.annual} annually</p>
                <ul className="space-y-3 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start space-x-2 text-sm text-[#374151]">
                      <i className="ri-check-line text-[#C41E3A] mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelected(p.name)}
                  className={`w-full py-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${selected === p.name ? 'bg-[#C41E3A] text-white' : 'border border-[#0C2340] text-[#0C2340] hover:bg-[#0C2340] hover:text-white'}`}
                >
                  {selected === p.name ? 'Selected' : 'Choose Plan'}
                </button>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">Sign Up Today</h2>
              {status === 'success' ? (
                <div className="text-center py-12 bg-green-50 rounded-xl border border-green-200">
                  <i className="ri-check-circle-fill text-4xl text-green-500 mb-3" />
                  <h3 className="text-xl font-bold text-[#111827]">You&apos;re Enrolled!</h3>
                  <p className="text-[#6B7280] mt-2">Our team will contact you within one business day to schedule your first visit.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="membership-form" className="space-y-4">
                  <input type="hidden" name="plan" value={selected} />
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Full Name *</label>
                    <input name="name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Email Address *</label>
                    <input name="email" type="email" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Phone Number *</label>
                    <input name="phone" type="tel" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Service Address *</label>
                    <input name="address" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="123 Main St, City, State" />
                  </div>
                  {status === 'error' && <p className="text-sm text-red-600">Something went wrong. Please try again.</p>}
                  <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#C41E3A] text-white py-4 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                    {status === 'submitting' ? 'Enrolling...' : `Enroll in ${selected}`}
                  </button>
                </form>
              )}
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">Why Get a Protection Plan?</h2>
              <div className="space-y-4">
                {[
                  { icon: 'ri-money-dollar-circle-line', title: 'Save on Repairs', desc: 'Members save an average of $400/year on service calls and parts.' },
                  { icon: 'ri-shield-check-line', title: 'Prevent Breakdowns', desc: 'Regular maintenance catches problems before they become emergencies.' },
                  { icon: 'ri-time-line', title: 'Priority Service', desc: 'Skip the queue. Plan members get priority scheduling and dispatch.' },
                  { icon: 'ri-home-smile-line', title: 'Extend Equipment Life', desc: 'Maintained systems last 30–50% longer than unmaintained ones.' },
                ].map((b) => (
                  <div key={b.title} className="flex items-start space-x-4 p-4 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0]">
                    <div className="w-10 h-10 bg-[#0C2340]/8 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${b.icon} text-xl text-[#0C2340]`} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#111827]">{b.title}</h4>
                      <p className="text-xs text-[#6B7280] mt-1">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate('/commercial')} className="mt-6 w-full border border-[#0C2340] text-[#0C2340] py-3 rounded-lg text-sm font-semibold hover:bg-[#0C2340] hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                View Commercial Contracts
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default MaintenancePlansPage;
